import {
  SimpleMessageService,
  WebAppCommonModule,
} from "@allbesmart/web-app-common";
import { DatePipe } from "@angular/common";
import {
  afterNextRender,
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Subscription } from "rxjs";
import { NewNoteDto } from "src/app/models/new-note-dto";
import { NoteDto } from "src/app/models/note-dto";
import { OrderDto } from "src/app/models/order-dto";
import { InitialsPipe } from "src/app/pipes/initials.pipe";
import { OrderService } from "src/app/services/order.service";

@Component({
  selector: "oaibox-order-notes-modal",
  templateUrl: "./order-notes-modal.component.html",
  styleUrl: "./order-notes-modal.component.css",
  imports: [WebAppCommonModule, ReactiveFormsModule, DatePipe, InitialsPipe],
})
export class OrderNotesModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input({ required: true }) order!: OrderDto;

  @ViewChild("notesList") private notesList?: ElementRef<HTMLOListElement>;

  protected readonly MESSAGE_MAX_LENGTH = 2000;

  protected notes: NoteDto[] = [];
  protected noteForm: NoteFormGroup;
  protected isSaving = false;

  private addNoteSubscription?: Subscription;

  constructor(
    private readonly orderService: OrderService,
    private readonly simpleMessageService: SimpleMessageService,
    private readonly injector: Injector
  ) {
    this.noteForm = new FormGroup({
      message: new FormControl<string | null>(null, [
        Validators.required,
        Validators.maxLength(this.MESSAGE_MAX_LENGTH),
        Validators.pattern(/\S/),
      ]),
    });
  }

  ngOnInit(): void {
    this.notes = [...(this.order.notes ?? [])];
  }

  ngAfterViewInit(): void {
    this.scrollToLatestNote();
  }

  protected addNote(): void {
    const message = this.noteForm.value.message;
    if (!message || this.isSaving) return;

    const newNoteDto: NewNoteDto = { message };

    this.isSaving = true;
    this.addNoteSubscription?.unsubscribe();
    this.addNoteSubscription = this.orderService
      .addNote(this.order.id, newNoteDto)
      .subscribe({
        next: (noteDto) => {
          this.isSaving = false;
          this.notes = [...this.notes, noteDto];
          this.noteForm.reset();
          this.orderService.refreshOrders();
          afterNextRender(() => this.scrollToLatestNote(), {
            injector: this.injector,
          });
        },
        error: () => {
          this.isSaving = false;
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to add note!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });
  }

  private scrollToLatestNote(): void {
    const notesList = this.notesList?.nativeElement;
    if (!notesList) return;

    notesList.scrollTop = notesList.scrollHeight;
  }

  ngOnDestroy(): void {
    this.addNoteSubscription?.unsubscribe();
  }
}

type NoteFormGroup = FormGroup<{
  message: FormControl<string | null>;
}>;
